// Empty-state hero. Sample prompts follow assistant-ui's documented welcome-screen
// pattern (see the shadcn `Thread` registry component): suggestions are registered
// on the runtime's `suggestions` config (see useNativeAgenthubExternalRuntime and
// entities/agent's toAgentThreadSuggestions) and rendered via
// ThreadPrimitive.Suggestion, wrapping the shared shadcn Button.

import { AlertTriangle, CalendarDays, ListOrdered, MessageCircle, Sparkle, TrendingUp, User, type LucideIcon } from 'lucide-react';
import { ThreadPrimitive } from '@assistant-ui/react';
import { useAgentCatalog } from '@/app/providers/AgentCatalogProvider';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import argusLogo from '../../../../assets/images/acs-argus-mark-192.png';

type ChatEmptyStateProps = {
  agentId: string;
};

const CATEGORY_ICON: Record<string, LucideIcon> = {
  'Account overview': User,
  'Portfolio prioritisation': ListOrdered,
  'Meeting preparation': CalendarDays,
  'Risks and renewals': AlertTriangle,
  'Adoption, value and growth': TrendingUp,
};
const DEFAULT_CATEGORY_ICON: LucideIcon = MessageCircle;

// Split in two so the caller (Thread.tsx) can interleave the composer between
// them — the required order is agent name, agent description, composer, then
// suggested prompts, and the composer isn't owned by this widget.

/** Agent name + description — the top of the empty-state stack. */
export function ChatEmptyStateHeader({ agentId }: ChatEmptyStateProps) {
  const { agents } = useAgentCatalog();
  const agent = agents.find((a) => a.id === agentId);
  const bullets = agent?.bullets ?? [];
  const description = agent?.description ?? '';
  const hasInfo = Boolean(description || bullets.length > 0);
  const isArgus = agentId === 'argus';

  return (
    <div className="flex flex-col items-center gap-5" role="status">
      <h1 className="flex items-center gap-3 text-3xl font-semibold text-foreground">
        {isArgus ? (
          <img src={argusLogo} alt="" aria-hidden className="size-10 object-contain" />
        ) : (
          <Sparkle className="size-7 fill-primary text-primary" />
        )}
        <span>{agent?.displayName ?? 'How can I help you today?'}</span>
      </h1>

      {hasInfo && (
        <div className="w-full max-w-lg rounded-lg bg-muted p-4 text-center shadow-sm">
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
          {bullets.length > 0 && (
            <ul className="mt-3 flex flex-wrap justify-center gap-2">
              {bullets.map((bullet) => (
                <li key={bullet}>
                  <Badge variant="outline" className="h-auto max-w-full whitespace-normal text-center">
                    {bullet}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const CUSTOMER_NAME_PLACEHOLDER = '[customer name]';

function PromptLabel({ prompt }: { prompt: string }) {
  const parts = prompt.split(CUSTOMER_NAME_PLACEHOLDER);

  if (parts.length === 1) {
    return <span className="whitespace-normal">{prompt}</span>;
  }

  return (
    <span className="whitespace-normal">
      {parts.map((part, index) => (
        <span key={`${part}-${index}`}>
          {part}
          {index < parts.length - 1 && (
            <span className="rounded bg-primary/10 px-1 font-semibold text-primary underline decoration-dotted underline-offset-2">
              {CUSTOMER_NAME_PLACEHOLDER}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

function SuggestionButton({ prompt }: { prompt: string }) {
  return (
    <ThreadPrimitive.Suggestion prompt={prompt} send={false} clearComposer={false} asChild>
      <Button
        variant="ghost"
        className="h-auto w-full justify-start rounded-lg bg-muted px-4 py-3 text-left text-sm font-normal whitespace-normal shadow-sm hover:bg-accent"
      >
        <PromptLabel prompt={prompt} />
      </Button>
    </ThreadPrimitive.Suggestion>
  );
}

/**
 * Suggested-prompt cards — rendered below the composer in the empty state.
 * When the agent has categorized prompts, each category gets its own header.
 * Prompt text is supplied directly so the visible placeholder styling can be
 * richer than the plain string sent to the composer.
 * Agents with only a flat prompt list fall back to an ungrouped grid.
 */
export function ChatEmptyStatePrompts({ agentId }: ChatEmptyStateProps) {
  const { agents } = useAgentCatalog();
  const agent = agents.find((a) => a.id === agentId);
  const categories = agent?.examplePromptCategories ?? [];

  if (categories.length === 0) {
    const prompts = agent?.examplePrompts ?? [];
    return (
      <div className="flex w-full max-w-2xl flex-col gap-5">
        <p className="text-xsm text-muted-foreground">Not sure where to start? Try one of these.</p>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {prompts.map((prompt) => <SuggestionButton key={prompt} prompt={prompt} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-2xl flex-col gap-5">
      <p className="text-xsm text-muted-foreground">Not sure where to start? Try one of these.</p>
      {categories.map(({ category, prompts }) => {
        const CategoryIcon = CATEGORY_ICON[category] ?? DEFAULT_CATEGORY_ICON;
        return (
          <div key={category} className="flex flex-col gap-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-primary">
                <CategoryIcon className="size-3.5" aria-hidden="true" />
              </span>
              {category}
            </h2>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {prompts.map((prompt) => <SuggestionButton key={prompt} prompt={prompt} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
